/**
 * Fetch wrapper for the Workspace API.
 *
 * Conventions
 *  - Prepends `/api/v1` to every path (or VITE_API_URL if set).
 *  - Adds `Authorization: Bearer <access>` when a token is stored.
 *  - On 401 with a refresh token available, attempts one silent refresh and
 *    retries the original request. If refresh fails, clears tokens and lets
 *    the caller surface the error.
 *  - Parses the backend's `{code, detail, errors}` envelope into an ApiError.
 */

import { ApiError } from "./errors";
import { emitTenantSignal } from "@/lib/tenant/signals";
import { API_BASE } from "@/lib/tenant/basePath";
import { tokenStorage } from "./tokens";


export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refresh = tokenStorage.getRefresh();
    if (!refresh) return false;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) {
        tokenStorage.clear();
        return false;
      }
      const data = (await res.json()) as {
        access: string;
        refresh?: string;
      };
      tokenStorage.set({
        access: data.access,
        refresh: data.refresh ?? refresh,
      });
      return true;
    } catch {
      tokenStorage.clear();
      return false;
    }
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

async function rawFetch(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
  retried = false,
): Promise<Response> {
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(body !== undefined && !isFormData
      ? { "Content-Type": "application/json" }
      : {}),
    ...opts.headers,
  };
  const access = tokenStorage.getAccess();
  if (access) headers.Authorization = `Bearer ${access}`;

  const init: RequestInit = {
    method,
    headers,
    signal: opts.signal,
  };
  if (body !== undefined) {
    init.body = isFormData ? (body as FormData) : JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 401 && !retried && tokenStorage.getRefresh()) {
    const ok = await refreshTokens();
    if (ok) return rawFetch(method, path, body, opts, true);
  }

  return res;
}

/**
 * A plain fetch with the app's auth: adds the Bearer token and, on 401, shares the one
 * silent refresh with the rest of the app before retrying once. For code that needs a
 * raw Response, such as the LoopDesk help panel (src/components/support/HelpLauncher.tsx).
 */
export async function authedFetch(input: string, init: RequestInit = {}, retried = false): Promise<Response> {
  const headers = new Headers(init.headers);
  const access = tokenStorage.getAccess();
  if (access) headers.set("Authorization", `Bearer ${access}`);
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401 && !retried && tokenStorage.getRefresh()) {
    const ok = await refreshTokens();
    if (ok) return authedFetch(input, init, true);
  }
  return res;
}

async function unwrap<T>(res: Response): Promise<ApiResponse<T>> {
  if (res.status === 204 || res.status === 205) {
    return {
      data: undefined as unknown as T,
      status: res.status,
      headers: res.headers,
    };
  }

  const ct = res.headers.get("Content-Type") ?? "";
  const body = ct.includes("application/json")
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    if (typeof body === "object" && body !== null) {
      const b = body as {
        code?: string;
        detail?: string;
        errors?: Record<string, string[]>;
        non_field_errors?: string[];
      };
      const code = b.code || `http_${res.status}`;
      // TL-066: a paused workspace or an unknown host swaps the whole screen.
      if (res.status === 402 && code === "workspace_paused") emitTenantSignal("paused");
      if (res.status === 404 && code === "tenant_not_found") emitTenantSignal("not_found");
      const detail =
        b.detail ||
        b.non_field_errors?.[0] ||
        (typeof body === "string" ? body : `HTTP ${res.status}`);
      throw new ApiError(code, detail, res.status, b.errors, b as Record<string, unknown>);
    }
    throw new ApiError(
      `http_${res.status}`,
      typeof body === "string" ? body : `HTTP ${res.status}`,
      res.status,
    );
  }

  return { data: body as T, status: res.status, headers: res.headers };
}

export const api = {
  async get<T>(path: string, opts?: RequestOptions): Promise<ApiResponse<T>> {
    return unwrap<T>(await rawFetch("GET", path, undefined, opts));
  },
  async post<T>(
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return unwrap<T>(await rawFetch("POST", path, body, opts));
  },
  async patch<T>(
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return unwrap<T>(await rawFetch("PATCH", path, body, opts));
  },
  async put<T>(
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return unwrap<T>(await rawFetch("PUT", path, body, opts));
  },
  async delete<T = void>(
    path: string,
    opts?: RequestOptions,
    body?: unknown,
  ): Promise<ApiResponse<T>> {
    return unwrap<T>(await rawFetch("DELETE", path, body, opts));
  },
  /** GET a file (PDF and so on) with auth. Errors still parse the JSON envelope. */
  async blob(path: string, opts?: RequestOptions): Promise<Blob> {
    const res = await rawFetch("GET", path, undefined, {
      ...opts,
      headers: { Accept: "*/*", ...opts?.headers },
    });
    if (!res.ok) {
      await unwrap<never>(res);
    }
    return res.blob();
  },
  /**
   * GET a file with auth, reporting download progress as bytes arrive
   * (`total` is null when the server sends no Content-Length).
   */
  async file(
    path: string,
    opts: RequestOptions & { onProgress?: (loaded: number, total: number | null) => void } = {},
  ): Promise<Blob> {
    const { onProgress, ...rest } = opts;
    const res = await rawFetch("GET", path, undefined, {
      ...rest,
      headers: { Accept: "*/*", ...rest.headers },
    });
    if (!res.ok) {
      await unwrap<never>(res);
    }
    const type = (res.headers.get("Content-Type") ?? "").split(";")[0].trim();
    if (!res.body || !onProgress) {
      const blob = await res.blob();
      return blob.type ? blob : new Blob([blob], { type });
    }
    const total = Number(res.headers.get("Content-Length")) || null;
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      onProgress(loaded, total);
    }
    return new Blob(chunks as BlobPart[], { type });
  },
};
